
import React, { useEffect, useState, useCallback } from "react";
import {
  onAuthStateChanged,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { auth, db } from "./FirebaseConfig";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { Navigate } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  message,
  Card,
  Divider,
  Typography,
  Alert,
} from "antd";
import StomataLoader from "./StomataLoader";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [passwordForm] = Form.useForm();
  const { i18n } = useTranslation();
  const t = useCallback((key, options) => i18n.t(key, options), [i18n]);

  const isPasswordUser = user?.providerData?.some(
    (provider) => provider.providerId === "password"
  );

  useEffect(() => {
    let active = true;

    const unsub = onAuthStateChanged(auth, async (cur) => {
      if (!active) return;
      setUser(cur);

      if (!cur) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const ref = doc(db, "users", cur.uid);
        const snap = await getDoc(ref);
        const data = snap.data() || {};

        if (!active) return;

        setInitial({
          displayName: data.displayName || cur.displayName || "",
          organization: data.organization || "",
          email: data.email || cur.email || "",
          location: data.location || "",
          campany: data.campany || "",
        });
      } catch (e) {
        console.error(e);
        message.error(t("profile.errors.fetch"));
      } finally {
        if (active) setLoading(false);
      }
    });

    return () => {
      active = false;
      unsub();
    };
  }, [t]);

  // 既存のプロフィール保存
  const onFinish = async (values) => {
    if (!user) return;

    setSaving(true);

    try {
      await setDoc(
        doc(db, "users", user.uid),
        {
          displayName: values.displayName || "",
          email: user.email || "",
          organization: values.organization || "",
          campany: values.campany || "",
          location: values.location || "",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      message.success(t("profile.messages.saved"));
    } catch (e) {
      console.error(e);
      message.error(t("profile.messages.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // パスワード変更
  const onChangePassword = async (values) => {
    const currentUser = auth.currentUser;

    if (!currentUser || !currentUser.email || !isPasswordUser) {
      message.error(t("profile.password.passwordOnly"));
      return;
    }

    setChangingPassword(true);

    try {
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        values.currentPassword
      );

      // 現在のパスワードで再認証
      await reauthenticateWithCredential(currentUser, credential);

      // Firebase Authentication のパスワードを変更
      await updatePassword(currentUser, values.newPassword);

      passwordForm.resetFields();

      message.success(t("profile.password.changed"));
    } catch (error) {
      console.error("Password update failed:", error.code);

      switch (error.code) {
        case "auth/invalid-credential":
        case "auth/wrong-password":
          message.error(t("profile.password.incorrect"));
          break;

        case "auth/weak-password":
          message.error(t("profile.password.weak"));
          break;

        case "auth/too-many-requests":
          message.error(
            t("profile.password.tooMany")
          );
          break;

        case "auth/network-request-failed":
          message.error(t("profile.password.network"));
          break;

        case "auth/requires-recent-login":
          message.error(
            t("profile.password.reauth")
          );
          break;

        default:
          message.error(t("profile.password.failed"));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="spin">
        <StomataLoader />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" />;

  return (
    <div style={{ maxWidth: 600, margin: "16px auto" }}>
      <Card title={t("profile.title")}>
        <Form
          layout="vertical"
          initialValues={initial || {}}
          onFinish={onFinish}
          disabled={!initial || saving}
        >
          <Form.Item
            label={t("profile.email")}
            tooltip={t("profile.emailTip")}
          >
            <Input value={user.email || ""} disabled />
          </Form.Item>

          <Form.Item
            name="displayName"
            label={t("profile.name")}
            rules={[
              {
                required: true,
                message: t("profile.errors.nameRequired"),
              },
            ]}
          >
            <Input placeholder={t("profile.namePlaceholder")} />
          </Form.Item>

          <Form.Item
            name="location"
            label={t("profile.location")}
          >
            <Input placeholder={t("profile.locationPlaceholder")} />
          </Form.Item>

          <Form.Item
            name="campany"
            label={t("profile.campany")}
          >
            <Input placeholder={t("profile.campanyPlaceholder")} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t("profile.save")}
            </Button>
          </Form.Item>
        </Form>

        <Divider />

        <Typography.Title level={5}>
          {t("profile.password.title")}
        </Typography.Title>

        {isPasswordUser ? (
          <>
            <Text type="secondary">
              {t("profile.password.description")}
            </Text>

            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={onChangePassword}
              style={{ marginTop: 20 }}
            >
              <Form.Item
                name="currentPassword"
                label={t("profile.password.current")}
                rules={[
                  {
                    required: true,
                    message: t("profile.password.currentRequired"),
                  },
                ]}
              >
                <Input.Password
                  autoComplete="current-password"
                  placeholder={t("profile.password.current")}
                />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label={t("profile.password.new")}
                dependencies={["currentPassword"]}
                rules={[
                  {
                    required: true,
                    message: t("profile.password.newRequired"),
                  },
                  {
                    min: 8,
                    message: t("profile.password.minimum"),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value ||
                        value !== getFieldValue("currentPassword")
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(
                          t("profile.password.different")
                        )
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  autoComplete="new-password"
                  placeholder={t("profile.password.placeholder")}
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label={t("profile.password.confirm")}
                dependencies={["newPassword"]}
                rules={[
                  {
                    required: true,
                    message: t("profile.password.confirmRequired"),
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        !value ||
                        getFieldValue("newPassword") === value
                      ) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error(t("profile.password.mismatch"))
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  autoComplete="new-password"
                  placeholder={t("profile.password.repeat")}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={changingPassword}
                >
                  {t("profile.password.submit")}
                </Button>
              </Form.Item>
            </Form>
          </>
        ) : (
          <Alert
            type="info"
            showIcon
            message={t("profile.password.externalTitle")}
            description={t("profile.password.externalDescription")}
          />
        )}
      </Card>
    </div>
  );
};

export default ProfilePage;
